import * as React from 'react';
import {
  DataGrid,
  GridColDef,
  GridFeatureMode,
  GridRowsProp,
  GridSelectionModel,
} from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import LinearProgress from '@mui/material/LinearProgress';

import './styles.scss';
import { MoreHoriz } from '@mui/icons-material';
import NoDataOverlay from '../no-data-overlay';

const DataGridStyled = styled(DataGrid)(({ theme }) => ({
  padding: '18px 21px 5px 21px',
  borderRadius: '6px',
  borderColor: theme.palette.primary.light,
  borderWidth: '1px',
  borderStyle: 'solid',
  '& .MuiDataGrid-columnHeaders': {
    lineHeight: '17px !important',
  },
  '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeadersInner': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover, & .MuiDataGrid-row.Mui-selected.Mui-hovered': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-cell': {
    borderColor: theme.palette.primary.light,
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus,& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
    display: 'none',
  },
}));
interface Props {
  rows: GridRowsProp;
  columns: GridColDef[];
  onRowSelection?: (rows: any) => void;
  checkboxSelection?: boolean;
  rowIdProperty?: string;
  loading?: boolean;
  hideFooter?: boolean;
  pagination?: true | undefined;
  pageSize?: number;
  page?: number;
  setPage?: (page: number) => void,
  rowsPerPageOptions?: number;
  rowCount?: number;
  paginationMode?: GridFeatureMode | undefined;
  onRowClick?: (rows: any) => void;
  disableSelectionOnClick?: boolean;
  selectedItems?: any[];
}

const CustomTable = ({
  rows, columns, onRowSelection, checkboxSelection, rowIdProperty, loading,
  hideFooter, pagination, rowsPerPageOptions, rowCount, paginationMode, pageSize,
  page, setPage, onRowClick, disableSelectionOnClick, selectedItems,
}: Props) => {
  const [selectionModel, setSelectionModel] = React.useState<GridSelectionModel>([]);

  const moreActionIcon = () => <MoreHoriz sx={{ color: 'primary.main' }} />;

  const setSelectRows = (selection: GridSelectionModel) => {
    if (onRowSelection) {
      const selectedIDs = new Set(selection);
      const selectedRows = rows.filter((row) => selectedIDs.has(row[rowIdProperty || 'id']));
      onRowSelection(selectedRows);
    }
  };

  React.useEffect(() => {
    if (selectedItems) {
      const ids = selectedItems.map((item) => item[rowIdProperty || 'id']);
      setSelectionModel(ids);
    }
  }, [selectedItems, rows]);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={{ flexGrow: 1 }}>
        <DataGridStyled
          autoHeight
          autoPageSize
          headerHeight={28}
          rowHeight={62}
          rows={rows}
          getRowId={(row) => row[rowIdProperty || 'id']}
          columns={columns}
          checkboxSelection={checkboxSelection}
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: NoDataOverlay,
            MoreActionsIcon: moreActionIcon,
          }}
          loading={loading}
          hideFooter={hideFooter}
          pagination={pagination}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => {
            if (setPage) {
              setPage(newPage);
            }
          }}
          rowsPerPageOptions={[rowsPerPageOptions ?? 100]}
          rowCount={rowCount}
          paginationMode={paginationMode ?? 'client'}
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          onSelectionModelChange={(newSelectionModel) => {
            setSelectRows(newSelectionModel);
          }}
          selectionModel={selectionModel}
          onRowClick={onRowClick}
          disableSelectionOnClick={disableSelectionOnClick}
        />
      </div>
    </div>
  );
};

CustomTable.defaultProps = {
  onRowSelection: null,
  checkboxSelection: false,
  rowIdProperty: 'id',
  loading: false,
  hideFooter: true,
  pagination: undefined,
  pageSize: 1,
  page: 1,
  setPage: null,
  rowsPerPageOptions: 100,
  rowCount: 100,
  paginationMode: 'client',
  onRowClick: null,
  disableSelectionOnClick: false,
  selectedItems: [],
};

export default CustomTable;
